/* global __CLIENT__*/
import PropTypes from 'prop-types';

import React, { Component } from 'react';
import { connect } from 'react-redux';
import merge from 'lodash/merge';
import immutableCreatorSelector from '../../selectors/immutableCreatorSelector';
import { Field, reduxForm } from 'redux-form';
import { validateAndDispatch } from '../../helpers/form';
import InputField from '../form/InputField';
import { getComponent } from '../types/Components';
import { ElementActions } from '../../actions/element';
import { currentProjectIdSelector } from '../../reducers/projects';
import { dependencyTypeIdSelector } from '../../reducers/relationshipTypes';
import { wbsRootElementIdSelector } from '../../reducers/elements';

// This is no longer used. See CreateNewTasks.js
import '../../styles/CreateElementNewModal.css';
import '../../styles/ReactSelect.css';

const initialValuesSelector = state => {
  const modal = state.modal.get('modals').last();
  const modalOptions = modal ? modal.get('options') : null;

  const intialValuesObject = {};
  if (modalOptions) {
    const modalOptionsJS = modalOptions.toJS();
    merge(intialValuesObject, modalOptionsJS);
  }
  return intialValuesObject;
};

const rootElementIdSelector = state => state.projects.get('rootElementId');
const modalsSelector = state => state.modal.get('modals');
const elementTypesSelector = state => state.elementTypes.get('elementTypes');

const CreateElementNewSelector = immutableCreatorSelector(
  [
    rootElementIdSelector,
    modalsSelector,
    elementTypesSelector,
    currentProjectIdSelector,
    dependencyTypeIdSelector,
    wbsRootElementIdSelector,
    initialValuesSelector,
  ],
  (
    rootElementId,
    modals,
    elementTypes,
    currentProjectId,
    relationshipTypeId,
    wbsRootElementId,
    initialValues,
  ) => ({
    rootElementId,
    modals,
    elementTypes,
    currentProjectId,
    relationshipTypeId,
    wbsRootElementId,
    initialValues,
  }),
);

@connect(CreateElementNewSelector)
@reduxForm({
  form: 'createElementNew',
  destroyOnUnmount: true,
})
export default class CreateElementNew extends Component {
  static propTypes = {
    currentProjectId: PropTypes.string.isRequired,
    elementTypes: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    modals: PropTypes.object.isRequired,
    reset: PropTypes.func,
    rootElementId: PropTypes.string.isRequired,
    submitting: PropTypes.bool,
    wbsRootElementId: PropTypes.string.isRequired,
  };

  buildProperties = elementType => {
    const properties = elementType.get('props');

    if (!properties || !properties.size) return <span />;

    let index = 1;

    return (
      <div style={{ marginBottom: 10 }}>
        {properties
          .sortBy(p => p.get('_order'))
          .map((prop, key) =>
            <div key={key}>
              {' '}{this.fieldSet(key, prop, index++)}{' '}
            </div>,
          )
          .toArray()}
      </div>
    );
  };

  fieldSet(k, val, index) {
    if (val && val.has('_type')) {
      if (val.get('_display') === 'false' || val.get('_display') === false) {
        return <div />;
      }
      const customHelpText = val.has('_helptext')
        ? val.get('_helptext')
        : false;
      const typeOptions = val.has('_typeOptions') && val.get('_typeOptions')
        ? val.get('_typeOptions').toJSON()
        : null;
      const defaultValue = val.get('_default');
      const TypeComponent = getComponent(val.get('_type'));

      const className =
        val.get('_type') === 'richtext'
          ? 'form-group-fix-temp row'
          : 'form-group row';

      return (
        <div
          className={className}
          style={{ marginBottom: 10 }}
          key={`${k}-${index}`}
        >
          <label
            className="col-sm-2 col-form-label"
            style={{ paddingLeft: 0, paddingRight: 10 }}
          >
            {k}
          </label>
          <div
            key={`${k}-${index}`}
            className="col-sm-10"
            style={{ padding: 0 }}
          >
            <Field
              name={k}
              key={`${k}-${index}`}
              component={TypeComponent.editor}
              defaultValue={defaultValue}
              value={null}
              uid="createElementNew"
              form="createElementNew"
              propertyKey={k}
              propTypeOptions={typeOptions}
              customHelpText={customHelpText}
              fieldIndex={index}
            />
          </div>
        </div>
      );
    }
    return null;
  }

  submit = async (values, dispatch) => {
    const {
      currentProjectId,
      reset,
      rootElementId,
      modals,
      elementTypes,
      wbsRootElementId,
    } = this.props;

    const elementTypeId = modals.last().getIn(['options', 'elementTypeId']);
    const elementType = elementTypes.get(elementTypeId);
    const elementTypeName = elementType.get('type');

    const propKeys = [];
    const props = {};

    elementType.get('props').forEach((prop, key) => {
      // eslint-disable-line array-callback-return
      const propValue =
        values[key] || elementType.getIn(['props', key, '_default']);
      if (!propValue) return; // eslint-disable-line array-callback-return
      const compType = prop.get('_type');
      const TypeComponent = getComponent(compType);
      if (TypeComponent && TypeComponent.skipAutoSave) {
        return; // eslint-disable-line array-callback-return
      }
      propKeys.push(key);
      props[key] = propValue;
    });

    let parentId = rootElementId;
    if (elementType.get('type') === 'Work Breakdown Structure') {
      parentId = wbsRootElementId;
    }

    const createElementFormObject = {
      name: values.name || null,
      type_id: elementTypeId,
      parent_id: parentId,
      project_id: currentProjectId,
      props,
    };

    const validation = {
      name: [{ rule: 'required', label: `${elementTypeName} name` }],
    };

    const res = await validateAndDispatch(
      dispatch,
      ElementActions.createElement(currentProjectId, createElementFormObject),
      validation,
      createElementFormObject,
    );

    // Extract the ids of the elements created
    const ids = res.result.result;

    // Iterate through props and call the post submit method
    elementType.get('props').map(async (prop, key) => {
      // eslint-disable-line array-callback-return
      const propValue =
        values[key] || elementType.getIn(['props', key, '_default']);
      if (!propValue) return; // eslint-disable-line array-callback-return
      const compType = prop.get('_type');
      const TypeComponent = getComponent(compType);
      if (TypeComponent && TypeComponent.postCreate) {
        const typeOptions = elementType
          .getIn(['props', key, '_typeOptions'])
          .toJS();
        await TypeComponent.postCreate(
          propValue,
          dispatch,
          currentProjectId,
          ids,
          typeOptions,
        );
      }
    });

    reset();
  };

  render() {
    const {
      handleSubmit,
      submitting,
      // reset,
      modals,
      elementTypes,
    } = this.props;

    const elementTypeId = modals.last().getIn(['options', 'elementTypeId']);
    const elementType = elementTypes.get(elementTypeId);
    if (!elementType) {
      return (
        <div className="fs-modal-content">
          The type is missing. Cannot load modal.
        </div>
      );
    }

    const elementTypeName = elementType.get('type');
    const elementTypeTitle =
      elementTypeName === 'Work Breakdown Structure'
        ? 'WBS Element'
        : elementTypeName;
    const disabled = submitting !== undefined ? submitting : false;

    return (
      <div className="create-element-new-modal fs-modal-content">
        <h4>
          Create New {elementTypeTitle}
        </h4>
        <div>
          <form>
            <div className="modal-form-wrapper">
              <Field
                name="name"
                type="textarea"
                component={InputField}
                label="Name(s)"
                rows="5"
                className="form-control"
                placeholder={`One ${elementTypeTitle} per line...`}
                style={{ marginBottom: 10, paddingRight: 10 }}
                wrapperClassName="form-group row"
                labelClassName="col-sm-2 col-form-label"
                // labelStyle={{ padding: 0 }}
                containerClassName="col-sm-10"
                containerStyle={{ padding: 0 }}
                autoFocus
              />

              {this.buildProperties(elementType)}
            </div>
            <div className="modal-form-button-wrapper">
              <button
                className="btn btn-primary modal-submit-btn"
                onClick={handleSubmit(this.submit)}
                disabled={disabled}
                data-test="createButton"
              >
                {submitting &&
                  <i className="fa fa-cog fa-spinner fa-spin" />}{' '}
                Create New {elementTypeTitle}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/modals/CreateElementNewModal.js